import {
  Box,
  Td,
  Tr,
  Flex,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import lodash from 'lodash';
import React, { useState } from 'react';

import {
  EditRecurringDateModal,
  EditRelativeRecurringDateModal,
} from '../EditRecurringDateModal';
import {
  EditIconButton,
  ResetIconButton,
  InfoIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  Select,
  formatSelectOptions,
} from 'packages/common-frontend/components/Select';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { WipConfig } from 'packages/creditAgreements/ui/types';

const { cloneDeep } = lodash;

type GlobalRecurringDateConfig = CreditAgreement.Config.GlobalRecurringDate.Config;
type RecurringDateSetupType = CreditAgreement.Config.GlobalRecurringDate.SetupType;

export type SourceType = 'reporting' | 'portCovs';

export const createStaticRecurringDateSetupShell = (): CreditAgreement.Config.GlobalRecurringDate.StaticRecurringDateSetup => {
  return {
    type: 'static',
    config: { recurringDateConfigs: [], exclusions: [] },
  };
};

const createRelativeRecurringDateSetupShell = (params: {
  source: SourceType;
}): CreditAgreement.Config.GlobalRecurringDate.RelativeRecurringDateSetup => {
  const { source } = params;
  const defaultFormula =
    source === 'reporting' ? 'reportingDate' : 'triggerDate';
  return {
    type: 'relative',
    triggerDateFormula: defaultFormula,
    config: { relativeRecurringDateConfig: [], exclusions: [] },
  };
};

export const ReportingRecurringDateSetupRow: React.FC<{
  wipConfig?: WipConfig;
  recurringDateConfig: GlobalRecurringDateConfig;
  basePropertyKey: string;
  source: SourceType;
  onSave: (newField: GlobalRecurringDateConfig) => void;
}> = ({ wipConfig, recurringDateConfig, basePropertyKey, source, onSave }) => {
  const { field, setup } = recurringDateConfig;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [setupTypeState, setSetupTypeState] = useState<RecurringDateSetupType>(
    setup.type,
  );
  const [
    triggerDateFormulaState,
    setTriggerDateFormulaState,
  ] = useState<string>(
    setup.type === 'relative' ? setup.triggerDateFormula : '',
  );

  const resetSetup = (): void => {
    if (!wipConfig) {
      return;
    }

    const relativeRecurringDateDefault = createRelativeRecurringDateSetupShell({
      source,
    });

    const defaultSetup =
      setupTypeState === 'static'
        ? createStaticRecurringDateSetupShell()
        : relativeRecurringDateDefault;

    if (setupTypeState === 'relative') {
      setTriggerDateFormulaState(
        relativeRecurringDateDefault.triggerDateFormula,
      );
    }

    wipConfig.formProps.setFieldValue(basePropertyKey, defaultSetup);
  };

  const updateTriggerDateFormula = (): void => {
    if (!wipConfig || setup.type !== 'relative') {
      return;
    }

    wipConfig.formProps.setFieldValue(basePropertyKey, {
      ...setup,
      triggerDateFormula: triggerDateFormulaState,
    });
  };

  const onDateModalSave = (
    newSetupConfig: CreditAgreement.Config.GlobalRecurringDate.RecurringDateSetupConfigs,
  ): void => {
    const dateConfigCopy = cloneDeep(recurringDateConfig);
    dateConfigCopy.setup.config = newSetupConfig;
    onSave(dateConfigCopy);
    setIsOpen(false);
  };

  return (
    <Tr>
      <Td colSpan={6}>
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <Flex mb="2" alignItems="center">
            <Text w="48" fontWeight="semibold">
              Actions:
            </Text>

            <EditIconButton
              ariaLabel="edit config"
              label="Edit Config"
              onClick={() => {
                setIsOpen(true);
              }}
            />

            <ResetIconButton
              ariaLabel="reset setup"
              label="Reset Setup"
              onClick={() => {
                resetSetup();
              }}
            />
          </Flex>
        )}

        <Flex alignItems="center" mb="2">
          <Text w="48" fontWeight="semibold">
            Setup Type:
            {wipConfig?.isMutateAllowed(basePropertyKey) &&
              setupTypeState !== setup.type && (
                <InfoIconButton
                  ariaLabel="need to reset"
                  label="Need to Reset"
                  h="0"
                />
              )}
          </Text>

          {wipConfig?.isMutateAllowed(basePropertyKey) ? (
            <Box w="80" mr="3">
              <Select
                value={setupTypeState}
                onChange={(val) => {
                  if (val) {
                    setSetupTypeState(val);
                  }
                }}
                options={formatSelectOptions(
                  CreditAgreement.Config.GlobalRecurringDate.SetupTypeIterable,
                  'title',
                )}
              />
            </Box>
          ) : (
            <Text>{setup.type}</Text>
          )}
        </Flex>

        {setup.type === 'relative' && (
          <Flex alignItems="center" mb="5">
            <Text w="48" fontWeight="semibold">
              Trigger Date Formula:
            </Text>

            {wipConfig?.isMutateAllowed(basePropertyKey) ? (
              <InputGroup w="80">
                <InputLeftElement pointerEvents="none">
                  <Icon icon="equals" />
                </InputLeftElement>
                <Input
                  type="text"
                  value={triggerDateFormulaState}
                  onChange={(e) => setTriggerDateFormulaState(e.target.value)}
                />
                <InputRightElement>
                  <Tooltip label="Click to Update">
                    <IconButton
                      aria-label="update formula"
                      variant="ghost"
                      disabled={
                        setup.triggerDateFormula === triggerDateFormulaState
                      }
                      icon={<Icon icon="floppy-disk-circle-arrow-right" />}
                      onClick={() => updateTriggerDateFormula()}
                    />
                  </Tooltip>
                </InputRightElement>
              </InputGroup>
            ) : (
              <Text>{setup.triggerDateFormula}</Text>
            )}
          </Flex>
        )}

        <Box maxH="48" overflowY="auto" as="pre">
          {JSON.stringify(setup, null, 4)}
        </Box>
      </Td>

      {setup.type === 'static' && (
        <EditRecurringDateModal
          isOpen={isOpen}
          onCancel={() => setIsOpen(false)}
          onSave={onDateModalSave}
          recurringDateConfig={setup.config}
          displayName={field}
        />
      )}

      {setup.type === 'relative' && (
        <EditRelativeRecurringDateModal
          isOpen={isOpen}
          onCancel={() => setIsOpen(false)}
          onSave={onDateModalSave}
          relativeDateConfig={setup.config}
          displayName={field}
        />
      )}
    </Tr>
  );
};
